'use client';

import Link from 'next/link';
import { useEffect, useId, useRef } from 'react';
import { useAuth } from './useAuth';
import { useLogin } from './LoginContext';
import { LoginLink } from './LoginLink';
import { LoginForm } from './LoginForm';
import { defaultLoginReturnTo } from './loginReturnTo';
import { navShowsAccountMenu } from './oauthRedirect';

const PANEL_ID = 'login-drawer-panel';

export function LoginDrawer() {
  const { user, profile, sessionReady, joinGateError, sessionError } = useAuth();
  const { open, openLogin, revealLogin, closeLogin, oauthError, clearOauthError } = useLogin();
  const rootRef = useRef<HTMLDivElement>(null);
  const headingId = useId();
  const showAccount = navShowsAccountMenu({
    sessionReady,
    user,
    profile,
    joinGate: !!joinGateError,
  });
  const displayError = oauthError || sessionError;

  useEffect(() => {
    if (sessionError) revealLogin();
  }, [sessionError, revealLogin]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        closeLogin();
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') closeLogin();
    }
    // Defer so the opening click does not immediately close the panel.
    const timer = window.setTimeout(() => {
      document.addEventListener('pointerdown', onPointerDown);
    }, 0);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, closeLogin]);

  if (showAccount) return null;

  return (
    <div className="login-drawer" ref={rootRef}>
      <button
        type="button"
        id="login-drawer-trigger"
        data-testid="login-drawer-trigger"
        className={`btn btn-primary login-drawer__trigger${open ? ' login-drawer__trigger--active' : ''}`}
        aria-controls={PANEL_ID}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => (open ? closeLogin() : openLogin())}
      >
        Sign in
      </button>
      <div
        id={PANEL_ID}
        className={`login-drawer__panel${open ? ' login-drawer__panel--open' : ''}`}
        role="dialog"
        aria-modal="false"
        aria-labelledby={headingId}
        aria-hidden={!open}
        hidden={!open}
      >
        <LoginForm
          headingId={headingId}
          externalError={displayError}
          onClearExternalError={clearOauthError}
          onSuccess={closeLogin}
        />
        <div className="login-drawer__meta">
          <ul>
            <li>
              <Link href="/legal/terms/" onClick={() => closeLogin()}>
                Terms of Use
              </Link>
            </li>
            <li>
              <Link href="/legal/privacy/" onClick={() => closeLogin()}>
                Privacy Policy
              </Link>
            </li>
            <li>
              <LoginLink returnTo={defaultLoginReturnTo()} className="login-drawer__meta-link">
                Bookmarkable sign-in page
              </LoginLink>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
